import React, { createContext, FC, useCallback, useState } from 'react'
import { v4 as UUID } from 'uuid'
import { DialogContextTypes } from '../../enums'
import { AlertDialog, DialogContainer, ShowDialogProps } from '../../helpers/message'
import { ShowDialogParams } from '../../types/util-types'

interface DialogContextProps {
  showDialog: (Content: any, options: any, id?: DialogContextTypes) => any
  getDialogs: (id: string) => any
  dialogsCustom: any
  showConfirmDialog: (
    title: string,
    text?: string,
    options?: { acceptText?: string | undefined; cancelText?: string | undefined },
    onConfirm?: (callback: () => void) => void,
    context?: DialogContextTypes,
  ) => any
}

interface DialogContextParams<T> extends ShowDialogParams<T> {
  title?: string
  isAutoCloseDisabled?: boolean
  pop?: (value: boolean) => void
  stableGutters?: boolean
  customComponentInTitle?: React.ReactNode
}

export const DialogContextCtx = createContext<DialogContextProps>(null!)

export const DialogWrapper = ({ key, children }) => {
  return <div>{children}</div>
}

export const DialogContextProvider: React.FC = ({ children }) => {
  const [dialogs, setDialogs] = useState({})
  const [dialogsCustom, setDialogCustom] = useState({})
  const closeDialog = useCallback(
    (data, id, pop, contextType = null) => {
      if (pop) {
        pop(data)
      }
      if (contextType) {
        setDialogCustom(dialogsCustomList => {
          const dialogList = dialogsCustomList
          if (dialogList[contextType]) {
            delete dialogList[contextType][id]
            return { ...dialogList }
          }
          return dialogList
        })
      } else {
        const dialogList = dialogs
        delete dialogList[id]
        setDialogs(dialogList)
      }
    },
    [dialogs],
  )

  function getDialogs(id: string) {
    return dialogsCustom[id]
  }

  function showDialog<T = any, K = any>(
    Content: FC<K & ShowDialogProps<T>>,
    options: DialogContextParams<K> = {},
    id?: DialogContextTypes,
  ) {
    if (id) {
      const idVal = `custom-${UUID()}`
      const { opts, componentProps, pop, isAutoCloseDisabled, title, customComponentInTitle } = options
      setDialogCustom(d => ({
        ...d,
        ...{
          [id]: {
            [idVal]: (
              <DialogContainer
                pop={data => closeDialog(data, idVal, pop, id)}
                content={Content}
                opts={opts}
                contentProps={componentProps}
                isAutoCloseDisabled={isAutoCloseDisabled}
                title={title}
                key={idVal}
                keyIndex={idVal}
                customComponentInTitle={customComponentInTitle}
              />
            ),
          },
        },
      }))
    } else {
      const idVal = `mainDialog-${UUID()}`
      const { opts, componentProps, pop, isAutoCloseDisabled, title, customComponentInTitle } = options
      setDialogs(d => ({
        ...d,
        ...{
          [idVal]: (
            <DialogContainer
              pop={data => closeDialog(data, idVal, pop)}
              content={Content}
              opts={opts}
              contentProps={componentProps}
              isAutoCloseDisabled={isAutoCloseDisabled}
              title={title}
              key={idVal}
              keyIndex={idVal}
              customComponentInTitle={customComponentInTitle}
            />
          ),
        },
      }))
    }
  }

  function showConfirmDialog(
    title: string,
    text?: string,
    {
      acceptText = undefined,
      cancelText = undefined,
    }: { acceptText?: string | undefined; cancelText?: string | undefined } = {},
    onConfirm?: (callback: () => void) => void,
    context?: DialogContextTypes,
  ) {
    showDialog(
      AlertDialog,
      {
        componentProps: { acceptText, cancelText, text: text, onConfirm: onConfirm },
        title: title,
      },
      context,
    )
  }

  const contextValue: DialogContextProps = {
    showDialog,
    getDialogs,
    dialogsCustom,
    showConfirmDialog,
  }

  return (
    <DialogContextCtx.Provider value={contextValue}>
      {Object.values(dialogs)}
      {children}
    </DialogContextCtx.Provider>
  )
}
